import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  CssBaseline,
} from "@mui/material";
import axios from "axios";
import CreditsBanner from "../Components/CreditsNudge";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import { Link } from "react-router-dom";
import { message } from "../Constants/message.js";
import EmailValidationCheckerContent from "../Contents/EmailValidationCheckerContent.js";
import CommentSection from "../Components/CommentSection.js";

function EmailValidator({ user, login, isLoggedIn }) {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [credits, setCredits] = useState({
    remaining: 0,
    availed: 0,
    total: 0,
    loading: true,
  });

  useEffect(() => {
    const primaryInputText = localStorage.getItem("primaryInputText");
    if (primaryInputText) {
      setEmail(primaryInputText);
      localStorage.removeItem("primaryInputText");
    }
  }, []);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleValidateEmail = async () => {
    setSuccess("");
    setError("");
    setResult(null);

    if (!isLoggedIn) {
      setError(message.loginPrompt);
      setTimeout(() => {
        login();
      }, 1500);
      return;
    }

    if (isLoggedIn && user.account_type.toLowerCase() === "free") {
      if (credits.remaining < 1) {
        setError(
          "Insufficient credits to generate emails. Please upgrade your plan."
        );
        return;
      }
    }
    if (!email.trim()) {
      setError("Please enter an email address to validate.");
      return;
    }

    // ✅ Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError("Please enter a valid email address format.");
      return;
    }

    if (credits.remaining <= 0) {
      setError("Not enough credits to validate email.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/email-validation-checker`,
        { email },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );

      setLoading(false);
      setResult(response.data.result || {});
      setSuccess(response.data.successMessage || "Validation completed.");

      // Fetch updated credits
      const creditsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/credit`
      );
      if (isLoggedIn && user.account_type.toLowerCase() === "free") {
        const creditsResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/credit/updateCredits`,
          { toolName: "email_validation_checker" }
        );
        setCredits({
          remaining: creditsResponse.data.remaining_credits || 0,
          availed: creditsResponse.data.availed_credits || 0,
          total: creditsResponse.data.total_credits || 0,
          loading: false,
        });
      } else if (isLoggedIn && user.account_type.toLowerCase() === "paid") {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/credit/updateUsage`,
          { toolName: "email_validation_checker" }
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to validate email. Please try again or contact support."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 3 }}>
        <ContentBox>
          <TitleText variant="h3" align="center">
            Validate Email Address 📧
          </TitleText>

          <SubtitleText variant="subtitle1" align="center">
            Enter an email address to check its validity
          </SubtitleText>

          <Box mb={3} sx={{ maxWidth: 400, mx: "auto" }}>
            <TextField
              fullWidth
              label="Enter Email Address"
              variant="outlined"
              value={email}
              onChange={handleEmailChange}
              placeholder="e.g. user@example.com"
              size="medium"
              id="email-validator"
              name="primary-textbox"
            />
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2, maxWidth: 400, mx: "auto" }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2, maxWidth: 400, mx: "auto" }}>
              {success}
            </Alert>
          )}

          {isLoggedIn && user.account_type.toLowerCase() === "free" ? (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              mt={2}
            >
              <b>⚠️ Free Plan Limitations:</b> For Free plan users, 1 credit
              will be deducted for each email validation.{" "}
              <b>All Paid plan users</b> enjoy this tool for free 🎁{" "}
              <b>without any credit deduction</b>.
            </Typography>
          ) : (
            isLoggedIn && (
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                mt={2}
              >
                <b>
                  🎁 Good news! For all Paid plan users, email validation is
                  completely complimentary — no credits will be deducted.
                </b>
              </Typography>
            )
          )}

          <Box display="flex" justifyContent="center" gap={2}>
            <StyledButton
              variant="contained"
              color="success"
              onClick={handleValidateEmail}
            >
              Validate Email
            </StyledButton>
          </Box>

          {result && (
            <Box mt={4} display="flex" justifyContent="center">
              <Box
                sx={{
                  p: 4,
                  maxWidth: 500,
                  width: "100%",
                  border: "1px solid #ddd",
                  borderRadius: "16px",
                  backgroundColor: "#fafafa",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  textAlign: "center",
                }}
              >
                <Typography variant="h5" fontWeight="bold" mb={3}>
                  📊 Email Validation Results
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                    justifyItems: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body1">
                    <b>Format</b>
                  </Typography>
                  <Typography
                    variant="body1"
                    color={result.formatValid ? "success.main" : "error.main"}
                  >
                    {result.formatValid ? "✅ Valid" : "❌ Invalid"}
                  </Typography>

                  <Typography variant="body1">
                    <b>MX Records</b>
                  </Typography>
                  <Typography
                    variant="body1"
                    color={result.mxFound ? "success.main" : "error.main"}
                  >
                    {result.mxFound ? "✅ Found" : "❌ Not Found"}
                  </Typography>

                  <Typography variant="body1">
                    <b>SMTP Server</b>
                  </Typography>
                  <Typography
                    variant="body1"
                    color={result.smtpCheck ? "success.main" : "error.main"}
                  >
                    {result.smtpCheck ? "✅ Available" : "❌ Unavailable"}
                  </Typography>

                  <Typography variant="body1">
                    <b>Disposable</b>
                  </Typography>
                  <Typography
                    variant="body1"
                    color={
                      !result.isDisposable ? "warning.main" : "success.main"
                    }
                  >
                    {!result.isDisposable ? "⚠️ Yes" : "✅ No"}
                  </Typography>

                  <Typography variant="body1">
                    <b>Typo</b>
                  </Typography>
                  <Typography
                    variant="body1"
                    color={!result.typo ? "warning.main" : "success.main"}
                  >
                    {!result.typo ? "⚠️ Yes" : "✅ No"}
                  </Typography>

                  <Typography variant="body1">
                    <b>Deliverable</b>
                  </Typography>
                  <Typography
                    variant="body1"
                    color={result.valid ? "success.main" : "error.main"}
                  >
                    {result.valid ? "✅ Yes" : "❌ No"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" align="center">
              <br></br>
              Need help?{" "}
              <Link to="/contact" color="primary">
                Contact support
              </Link>
            </Typography>
          </Box>
        </ContentBox>
        <CommentSection
          toolId="email-validation-checker"
          user={user}
          login={login}
          isLoggedIn={isLoggedIn}
        />
        <EmailValidationCheckerContent />
      </PageContainer>
    </>
  );
}

export default EmailValidator;
